import { useState, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import {
  sendSignInLinkToEmail, AuthErrorCodes
} from 'firebase/auth';
import { auth } from '../../firebase';
import { navigate } from 'gatsby';
import authServiceInvoicePortal from '../../lib/authServiceInvoicePortal';
import {
  clearSessionAndLocalStorage,
  getFromStorage,
  removeFromStorage,
  setInStorage,
} from '../../util/storage-utils';
import routes from '../../routes';
import { useLocation } from '@reach/router';
import useContentMgmt from '../useContentMgmt';
import { FirebaseError } from 'firebase/app';
import useFormState from '../useFormState';
import SignInValidationRules, {
  Model,
  createPasswordResetValidationFunction,
} from '../../components/invoice-payment-portal/auth/SignInValidation.rules';
import useTransition from '../useTransition';
import { AuthState } from '../../lib/AuthenticationService/types';
import microcopyGroupIds from '../../components/pge-plus-common/microcopyGroupIds';
import useUtil from './useUtil';
import apolloClient from '../../lib/apolloClient';

type QueryParams = {
  jobRequest?: string;
  mode?: string;
  oobCode?: string;
  continueUrl?: string;
  lang?: string;
};

const invoicePaymentEmailKey = 'invoicePaymentEmail';

export const INVOICE_PAYMENT_SIGNIN_EMAIL_ID_STORAGE_KEY = 'emailForInvoicePaymentSignIn';

const useInvoicePaymentAuth = () => {
  const [authState, setState] = useState<AuthState>(
    authServiceInvoicePortal.getState(),
  );
  const { content } = useContentMgmt(
    microcopyGroupIds.INVOICE_PAYMENT_SIGN_IN,
  );
  const signInWithCustomToken = authServiceInvoicePortal.signInWithCustomToken.bind(
    authServiceInvoicePortal,
    );
  
  const { hideTransition, showTransition } = useTransition();

  const location = useLocation();

  const emailFromStorage: string | undefined = useMemo(
    () => getFromStorage(invoicePaymentEmailKey),
    [],
  );
  const defaultEmail = emailFromStorage;

  const form = useFormState(
    {
      email: defaultEmail || '',
      password: '',
      rememberMe: true,
    },
    {
      validate: SignInValidationRules,
      validationContext: {
        content,
      },
    },
  );

  const passwordResetForm = useFormState(
    {
      email: '',
      password: '',
      confirmPassword: '',
    },
    {
      validate: createPasswordResetValidationFunction,
      validationContext: {
        content,
      },
    },
  );

  useEffect(() => {
    return authServiceInvoicePortal.subscribe(setState);
  }, []);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setErrorNotification } = useUtil();
  const queryParams = queryString.parse(location.search) as QueryParams;

  const sendSignInLink = async (email: string) => {
    const baseURL = window.location.origin;
    const state = location?.state as any;
    const continueURL = Boolean(state?.redirectUrl)
      ? baseURL + state.redirectUrl
      : baseURL + routes.INVOICE_PAYMENT_PORTAL_INVOICE_LIST;

    const actionCodeSettings = {
      url: continueURL,
      // This URL should be the URL where you want to redirect the user after they click the sign-in link in their email.
      handleCodeInApp: true,
    };

    try {
      setIsLoading(true);
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      setErrorNotification(
        true,
        content.get('INSTELLER_SIGN_IN_LINK_SENT'),
        'success',
      );
      setIsLoading(false);

      window.localStorage.setItem(INVOICE_PAYMENT_SIGNIN_EMAIL_ID_STORAGE_KEY, email);
    } catch (err) {
      setIsLoading(false);

      if (
        (err as FirebaseError)?.code === AuthErrorCodes.ADMIN_ONLY_OPERATION
      ) {
        setErrorNotification(
          true,
          content.get('INSTELLER_SIGN_IN_LINK_SENT'),
          'success',
        );
        setIsLoading(false);
        return;
      }

      setErrorMessage(content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE') || '');
    }
  };

  const handleRedirect = () => {
    if (queryParams.continueUrl) {
      void navigate(
        queryParams.continueUrl.replace(window.location.origin, ''),
      );
    } else {
      void navigate(routes.INVOICE_PAYMENT_PORTAL_INVOICE_LIST);
    }
  };

  //const signOut = authServiceInvoicePortal.signOut.bind(authServiceInvoicePortal);
  const handleSignOut = async () => {
    // Queries need to be stopped before the store is cleared, if a query is in-flight,
    // an error will be returned and we will respond with an error page
    //stopAllQueries();
    await apolloClient.stop();
    await apolloClient.clearStore();
    clearSessionAndLocalStorage();
    authServiceInvoicePortal.signOut();
  };

  const signOut = async () => {
    await handleSignOut();
    await navigate(routes.INVOICE_PAYMENT_PORTAL_SIGN_IN);    
  };

  const refreshTokens = authServiceInvoicePortal.refreshTokens.bind(
    authServiceInvoicePortal,
  );
  const startTokenRefresher = authServiceInvoicePortal.startTokenRefresher.bind(
    authServiceInvoicePortal,
  );
  const stopTokenRefresher = authServiceInvoicePortal.stopTokenRefresher.bind(
    authServiceInvoicePortal,
  );

  const handleLogin = async (data: Model) => {
    showTransition(content.get('SUBMITTING_REQUEST') || '');
    try {
      handleRememberMe(Boolean(data.rememberMe), data.email);
      await authServiceInvoicePortal.signInWithPassword(data.email, data.password);

      handleRedirect();
      hideTransition();
    } catch (err) {
      showErrorMessage(err as Error);
      hideTransition();
    }
  };

  const showErrorMessage = (error: Error): void => {
    switch (error.message) {
      case 'EMAIL_NOT_FOUND':
        form.setError('email', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case 'INVALID_PASSWORD':
        form.setError('password', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case 'TOO_MANY_ATTEMPTS_TRY_LATER':
        form.setError('password', content.get('ERROR_AUTH_EXCEEDED_ATTEMPTS'));
        break;
      case AuthErrorCodes.INVALID_PASSWORD:
        form.setError('password', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AuthErrorCodes.USER_DELETED:
        form.setError('email', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
        form.setError('password', content.get('ERROR_AUTH_EXCEEDED_ATTEMPTS'));
        break;

      default:
        console.warn('unhandled auth exception', error);
        form.setError('password', content.get('ERROR_AUTH_UNEXPECTED'));
    }
  };

  const handleRememberMe = (rememberMe: boolean, email: string): void => {
    if (rememberMe) {
      setInStorage(invoicePaymentEmailKey, email);
    } else {
      removeFromStorage(invoicePaymentEmailKey);
    }
  };

  const handleLoginWithPassword = form.submit(handleLogin);

 
  return {
    ...authState,
    form,
    passwordResetForm,
    isLoading,
    authUser: auth?.currentUser,
    errorMessage,
    signOut,
    startTokenRefresher,
    stopTokenRefresher,
    refreshTokens,
    sendSignInLink,
    setErrorMessage,
    handleLoginWithPassword,   
    signInWithCustomToken
  };
};

export default useInvoicePaymentAuth;
